"use client";

import { useEffect, useState } from "react";
import { useInView } from "react-intersection-observer";
import { motion } from "framer-motion";
import Icon from "@/components/ui/AppIcon";

interface Metric {
  label: string;
  value: number;
  suffix: string;
  icon: string;
}

const metrics: Metric[] = [
  { label: "Orders Fulfilled", value: 750000, suffix: "+", icon: "CheckCircleIcon" },
  { label: "Active Brands", value: 450, suffix: "+", icon: "BuildingStorefrontIcon" },
  { label: "Countries Served", value: 90, suffix: "+", icon: "GlobeAltIcon" },
  { label: "Avg Response Time", value: 2, suffix: "h", icon: "ClockIcon" },
];

const comparisonData = [
  { route: "China → US", helium: "5-8 days", typical: "12-20 days" },
  { route: "China → EU", helium: "5-8 days", typical: "15-25 days" },
  { route: "China → UK", helium: "4-7 days", typical: "10-18 days" },
  { route: "China → Australia", helium: "6-9 days", typical: "14-22 days" },
];

function AnimatedCounter({ value, suffix, inView }: { value: number; suffix: string; inView: boolean }) {
  const [count, setCount] = useState(0);

  useEffect(() => {
    if (!inView) return;
    let start = 0;
    const duration = 2000;
    const increment = value / (duration / 16);
    const timer = setInterval(() => {
      start += increment;
      if (start >= value) {
        setCount(value);
        clearInterval(timer);
      } else {
        setCount(Math.floor(start));
      }
    }, 16);
    return () => clearInterval(timer);
  }, [inView, value]);

  return (
    <span>
      {count.toLocaleString()}{suffix}
    </span>
  );
}

export default function EnterpriseTrustSection() {
  const { ref, inView } = useInView({
    threshold: 0.2,
    triggerOnce: true,
  });

  return (
    <section className="py-28 bg-deep-dark relative overflow-hidden" ref={ref}>
      {/* Subtle background glow */}
      <div 
        className="absolute inset-0 pointer-events-none opacity-15"
        style={{
          background: "radial-gradient(ellipse 70% 50% at 50% 50%, rgba(91, 46, 255, 0.2) 0%, transparent 70%)",
        }}
      />

      <div className="max-w-7xl mx-auto px-6 lg:px-8 relative z-10">
        {/* Header */}
        <motion.div 
          className="text-center mb-16"
          initial={{ opacity: 0, y: 30 }}
          animate={inView ? { opacity: 1, y: 0 } : {}}
          transition={{ duration: 0.6 }}
        >
          <h2 
            className="font-heading font-extrabold text-text-primary mb-4"
            style={{ fontSize: "clamp(32px, 4vw, 52px)", letterSpacing: "-0.015em" }}
          >
            Infrastructure behind modern ecommerce brands
          </h2>
          <p className="text-lg text-text-secondary max-w-2xl mx-auto">
            From early-stage startups to established ecommerce brands — built on operational excellence and tech-driven reliability.
          </p>
        </motion.div>

        {/* Brand Logo Strip */}
        <motion.div 
          className="mb-20 overflow-hidden"
          initial={{ opacity: 0 }}
          animate={inView ? { opacity: 1 } : {}}
          transition={{ duration: 0.8, delay: 0.2 }}
        >
          <div className="flex items-center justify-center gap-12 flex-wrap">
            {[
              "Voltix Technologies",
              "Northvale Performance",
              "Lumae Laboratories",
              "Nexora Systems",
              "Avenor Group",
              "Sorell Studio"
            ].map((brand, idx) => (
              <motion.div 
                key={idx}
                className="text-text-secondary font-semibold text-lg tracking-wide transition-all duration-300 hover:text-white group relative"
                initial={{ opacity: 0, y: 20 }}
                animate={inView ? { opacity: 1, y: 0 } : {}}
                transition={{ duration: 0.6, delay: 0.3 + idx * 0.1 }}
                style={{
                  filter: "grayscale(100%)",
                  opacity: 0.6
                }}
              >
                {brand}
                {/* Subtle hover glow */}
                <div 
                  className="absolute inset-0 opacity-0 group-hover:opacity-100 transition-opacity duration-300 pointer-events-none"
                  style={{
                    background: "radial-gradient(circle, rgba(91, 46, 255, 0.3) 0%, transparent 70%)",
                    filter: "blur(20px)",
                    transform: "translateY(-50%)"
                  }}
                />
              </motion.div>
            ))}
          </div>
        </motion.div>

        {/* Animated Metrics Counters */}
        <motion.div 
          className="grid grid-cols-2 md:grid-cols-4 gap-6 mb-20"
          initial={{ opacity: 0, y: 30 }}
          animate={inView ? { opacity: 1, y: 0 } : {}}
          transition={{ duration: 0.6, delay: 0.4 }}
        >
          {metrics.map((metric, idx) => (
            <div 
              key={idx}
              className="glass-card p-8 text-center group"
            >
              <div className="w-12 h-12 rounded-2xl bg-glass-white border border-glass-border flex items-center justify-center mx-auto mb-4 group-hover:scale-110 transition-smooth">
                <Icon name={metric.icon} size={24} className="text-accent-primary" />
              </div>
              <div className="text-4xl font-bold text-accent-primary mb-2">
                <AnimatedCounter value={metric.value} suffix={metric.suffix} inView={inView} />
              </div>
              <div className="text-sm text-text-secondary">{metric.label}</div>
            </div>
          ))}
        </motion.div>

        {/* Delivery Time Comparison Table */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          animate={inView ? { opacity: 1, y: 0 } : {}}
          transition={{ duration: 0.6, delay: 0.6 }}
        >
          <h3 
            className="font-heading font-bold text-text-primary text-center mb-8"
            style={{ fontSize: "clamp(24px, 3vw, 36px)", letterSpacing: "-0.01em" }}
          >
            Delivery Time Comparison
          </h3>
          <div 
            className="overflow-hidden rounded-3xl"
            style={{
              background: "rgba(255, 255, 255, 0.03)",
              backdropFilter: "blur(20px)",
              border: "1px solid rgba(255, 255, 255, 0.08)",
            }}
          >
            <table className="w-full">
              <thead>
                <tr 
                  className="border-b"
                  style={{ borderColor: "rgba(255, 255, 255, 0.08)" }}
                >
                  <th className="text-left py-5 px-8 text-text-secondary font-semibold text-sm">Route</th>
                  <th className="text-center py-5 px-8 text-accent-primary font-bold text-sm">Helium</th>
                  <th className="text-center py-5 px-8 text-text-secondary font-semibold text-sm">Typical 3PL</th>
                </tr>
              </thead>
              <tbody>
                {comparisonData.map((row, idx) => (
                  <tr 
                    key={idx}
                    className="border-b transition-colors hover:bg-glass-white"
                    style={{ borderColor: "rgba(255, 255, 255, 0.05)" }}
                  >
                    <td className="py-5 px-8 text-text-primary font-medium">{row.route}</td>
                    <td className="text-center py-5 px-8">
                      <span className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-accent-primary bg-opacity-10 border border-accent-primary text-accent-primary font-bold text-sm">
                        <Icon name="BoltIcon" size={14} />
                        {row.helium}
                      </span>
                    </td>
                    <td className="text-center py-5 px-8 text-text-secondary">{row.typical}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </motion.div>
      </div>
    </section>
  );
}